import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import ScaApiService from '../../services/sca/ScaApiService';
import DashboardApiService from '../../services/dashboard/DashboardApiService';

const SCA = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchingAgents, setFetchingAgents] = useState(true);

  // Fetch agents on component mount
  useEffect(() => {
    fetchAgents();
  }, []);

  // Set default agent when agents are loaded
  useEffect(() => {
    if (agents.length > 0 && !selectedAgents) {
      setSelectedAgents(agents[0].id); // Set first agent as default
    }
  }, [agents]);

  const fetchAgents = async () => {
    try {
      setFetchingAgents(true);
      const agentsData = await DashboardApiService.getAllAgents();
      setAgents(agentsData || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setError('Failed to fetch agents');
    } finally {
      setFetchingAgents(false);
    }
  };

  const handleFetchSca = async () => {
    if (!selectedAgents) {
      setError('Please select an agent');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await ScaApiService.getScaByAgent(selectedAgents);

      // Open SCA details in new tab
      if (response && response.sca_summary) {
        const policyId = Array.isArray(response.sca_summary) ? response.sca_summary[0]?.policy_id : response.sca_summary.policy_id;
        if (policyId) {
          window.open(`/sca-details/${selectedAgents}?policy=${policyId}&filter=all`, '_blank');
        }
      }
    } catch (error) {
      console.error('Error fetching SCA data:', error);
      setError('Failed to fetch SCA data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score, total) => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
        >
          <VerifiedUserIcon sx={{ mr: 2, verticalAlign: 'middle', color: '#6366f1' }} />
          Security Configuration Assessment
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Evaluate security configuration compliance across your infrastructure
        </Typography>
      </Box>

      {/* Control Panel */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={9}>
              <FormControl fullWidth disabled={fetchingAgents}>
                <Select
                  value={selectedAgents}
                  onChange={(e) => setSelectedAgents(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    const agent = agents.find(a => a.id === selected);
                    if (!agent) {
                      return (
                        <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Select an Agent for Security Configuration Assessment
                        </Typography>
                      );
                    }
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          icon={<SecurityIcon />}
                          label={agent.name}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    );
                  }}
                  sx={{ 
                    backgroundColor: 'white',
                    height: '64px',
                    fontSize: '1rem',
                    '& .MuiSelect-select': {
                      padding: '18px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '64px',
                      boxSizing: 'border-box'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '2px',
                      borderColor: '#d1d5db'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        width: 'auto',
                        zIndex: 9999
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left'
                    }
                  }}
                >
                  {agents.map((agent) => (
                    <MenuItem 
                      key={agent.id} 
                      value={agent.id}
                      sx={{ 
                        minHeight: '56px',
                        padding: '12px 16px',
                        '&:hover': {
                          backgroundColor: '#f3f4f6'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Chip 
                          icon={<SecurityIcon />} 
                          label={agent.name} 
                          size="medium" 
                          sx={{ mr: 2, fontWeight: 'bold' }} 
                        />
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {agent.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            IP: {agent.ip} • Status: {agent.status || 'Active'}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={handleFetchSca}
                disabled={!selectedAgents || loading || fetchingAgents}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
                sx={{ 
                  height: '64px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #4f46e5, #7c3aed)',
                  }
                }}
              >
                {loading ? 'Analyzing...' : 'RUN ASSESSMENT'}
              </Button>
            </Grid>
          </Grid>

          {fetchingAgents && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, p: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Loading agents...</Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>


      {/* Enhanced Instructions Card */}
      {!loading && (
        <Card sx={{ 
          background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 50%, #a5b4fc 100%)',
          border: '1px solid #c7d2fe',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)'
        }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              mb: 3,
              boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)'
            }}>
              <VerifiedUserIcon sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            
            <Typography variant="h4" sx={{ 
              fontWeight: 'bold', 
              mb: 2,
              background: 'linear-gradient(45deg, #1e293b, #475569)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}>
              Security Configuration Assessment
            </Typography>
            
            <Typography variant="h6" sx={{ color: '#475569', mb: 4, maxWidth: 600, mx: 'auto' }}>
              Comprehensive security compliance evaluation for your infrastructure. 
              An agent is pre-selected - click "Run Assessment" to begin analysis.
            </Typography>

            <Grid container spacing={3} sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}>
                  <CheckCircleIcon sx={{ fontSize: 40, color: '#16a34a', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
                    Pass Checks
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Identify compliant security configurations
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}>
                  <ErrorIcon sx={{ fontSize: 40, color: '#dc2626', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
                    Failed Checks
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Detect security misconfigurations
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}>
                  <AssessmentIcon sx={{ fontSize: 40, color: '#6366f1', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
                    Compliance Score
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Overall security posture rating
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ 
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.5)'
            }}>
              <SecurityIcon sx={{ color: '#6366f1' }} />
              <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: '500' }}>
                Ready to analyze security configurations
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default SCA;
